
import ByteView from '../index.js'

const POOL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
const URL_POOL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
const URL_POOL99 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()+_-=}{[]|:;"/?.><,`~'

/**
 *
 * @param {string} pool
 * @returns {[Array<string>, Array<number>]}
 */
function reduceToLookup (pool) {
  const { length } = pool
  let index = -1
  const lookupTables = [[], []]

  while (++index < length) {
    lookupTables[0][index] = pool[index]
    lookupTables[1][pool.charCodeAt(index)] = index
  }

  return lookupTables
}

const base64Table = reduceToLookup(POOL)
const base64urlTable = reduceToLookup(URL_POOL)

export class Base64ByteIterator {
  #string
  #encoding
  #alphabet

  constructor (string, encoding = 'base64') {
    if (typeof string !== 'string') {
      throw new TypeError('Expected typeof "string". Recieved typeof: "' + typeof string + '"')
    }
    this.#string = string
    this.#encoding = encoding
    this.#alphabet = this.#encoding === 'base64url' ? base64urlTable : base64Table
  }

  * [Symbol.iterator] () {
    if (this.#encoding === 'base64url') {
      this.#string = padString(this.#string)
    }

    const [validLength, placeHoldersLength] = parseLengths(this.#string)
    let value = 0
    let index = -1
    const lengthOfBytes = placeHoldersLength > 0 ? validLength - 4 : validLength

    while (index + 1 < lengthOfBytes) {
      value = (
        (this.#alphabet[1][this.#string.charCodeAt(++index)] << 18) |
        (this.#alphabet[1][this.#string.charCodeAt(++index)] << 12) |
        (this.#alphabet[1][this.#string.charCodeAt(++index)] << 6) |
        this.#alphabet[1][this.#string.charCodeAt(++index)]
      )
      yield * [
        (value >> 16) & 0xFF,
        (value >> 8) & 0xFF,
        value & 0xFF
      ]
    }

    if (placeHoldersLength === 2) {
      value = (
        (this.#alphabet[1][this.#string.charCodeAt(++index)] << 2) |
        (this.#alphabet[1][this.#string.charCodeAt(++index)] >> 4)
      )
      yield value & 0xFF
    }

    if (placeHoldersLength === 1) {
      value = (
        (this.#alphabet[1][this.#string.charCodeAt(++index)] << 10) |
        (this.#alphabet[1][this.#string.charCodeAt(++index)] << 4) |
        (this.#alphabet[1][this.#string.charCodeAt(++index)] >> 2)
      )
      yield * [
        (value >> 8) & 0xFF,
        value & 0xFF
      ]
    }
  }
}

export default class Base64 {
  #encoding
  #alphabet

  static SEGMENT_LENGTH = 4

  constructor (encoding = 'base64') {
    this.#encoding = encoding
    this.#alphabet = encoding === 'base64'
      ? reduceToLookup(POOL)
      : encoding === 'base64url' ? reduceToLookup(URL_POOL) : reduceToLookup(POOL)
  }

  get [Symbol.toStringTag] () {
    return 'Base64'
  }

  get encoding () {
    return this.#encoding
  }

  byteLength (string) {
    const [validLength, placeHoldersLength] = parseLengths(string)

    return (((validLength + placeHoldersLength) * 3) / 4) - placeHoldersLength
  }

  decode (string) {
    if (this.#encoding === 'base64url') {
      string = this.padString(string)
    }

    const [validLength, placeHoldersLength] = parseLengths(string)
    let value = 0
    const bytes = new ByteView(
      (((validLength + placeHoldersLength) * 3) / 4) - placeHoldersLength
    )

    let index = -1
    let byteIndex = -1
    const lengthOfBytes = placeHoldersLength > 0 ? validLength - 4 : validLength

    while (index + 1 < lengthOfBytes) {
      value = (
        (this.#alphabet[1][string.charCodeAt(++index)] << 18) |
        (this.#alphabet[1][string.charCodeAt(++index)] << 12) |
        (this.#alphabet[1][string.charCodeAt(++index)] << 6) |
        this.#alphabet[1][string.charCodeAt(++index)]
      )
      bytes[++byteIndex] = (value >> 16) & 0xFF
      bytes[++byteIndex] = (value >> 8) & 0xFF
      bytes[++byteIndex] = value & 0xFF
    }

    if (placeHoldersLength === 2) {
      value = (
        (this.#alphabet[1][string.charCodeAt(++index)] << 2) |
        (this.#alphabet[1][string.charCodeAt(++index)] >> 4)
      )
      bytes[++byteIndex] = value & 0xFF
    }

    if (placeHoldersLength === 1) {
      value = (
        (this.#alphabet[1][string.charCodeAt(++index)] << 10) |
        (this.#alphabet[1][string.charCodeAt(++index)] << 4) |
        (this.#alphabet[1][string.charCodeAt(++index)] >> 2)
      )
      bytes[++byteIndex] = (value >> 8) & 0xFF
      bytes[++byteIndex] = value & 0xFF
    }

    return bytes
  }

  encode (byteView) {
    const { length } = byteView
    const extraBytes = length % 3
    const chunksLength = length - extraBytes
    let index = -1
    let bits = 0
    let value = 0
    let response = ''

    while (index + 3 < chunksLength) {
      let temp = (
        ((byteView[++index] << 16) & 0xFF0000) +
        ((byteView[++index] << 8) & 0xFF00) +
        (byteView[++index] & 0xFF)
      )

      response += (
        this.#alphabet[0][temp >> 18 & 0x3F] +
        this.#alphabet[0][temp >> 12 & 0x3F] +
        this.#alphabet[0][temp >> 6 & 0x3F] +
        this.#alphabet[0][temp & 0x3F]
      )
    }

    while (++index < length) {
      const byte = byteView[index]
      value = (value << 0x8) | byte
      bits += 8

      while (bits >= 6) {
        response += this.#alphabet[0][(value >>> (bits - 6)) & 0x3F]
        bits -= 6
      }
    }

    if (bits > 0) {
      response += this.#alphabet[0][(value << (6 - bits)) & 0x3F]
    }

    if (this.encoding === 'base64url') return response

    if (extraBytes === 1) {
      response += '=='
    } else if (extraBytes === 2) {
      response += '='
    }

    return response
  }

  /*
  encode (byteView) {
    const { length } = byteView
    let index = -1
    let bits = 0
    let value = 0
    let response = ''

    while (++index < length) {
      const byte = byteView[index]
      value = (value << 0x8) | byte
      bits += 8

      while (bits >= 6) {
        response += this.#alphabet[(value >>> (bits - 6)) & 0x3F]
        bits -= 6
      }
    }

    if (bits > 0) {
      response += this.#alphabet[(value << (6 - bits)) & 0x3F]
    }

    if (this.encoding === 'base64url') return response

    const extraBytes = length % 3
    if (extraBytes === 1) {
      response += '=='
    } else if (extraBytes === 2) {
      response += '='
    }

    return response
  }
  */

  padString (string) {
    const { length } = string
    const difference = length % Base64.SEGMENT_LENGTH

    if (!difference) {
      return string
    }

    let position = length
    let padLength = Base64.SEGMENT_LENGTH - difference
    const byteView = ByteView.alloc(length + padLength)
    byteView.write(string)

    while (padLength--) {
      byteView.write('=', position++)
    }

    return byteView.toString()
  }

  encode90 (byteView) {
    const { length } = byteView
    let index = -1
    let response = ''

    while (++index < length) {
      response += URL_POOL99[byteView[index] % 92]
    }

    return response
  }
}

function padString (string) {
  const { length } = string
  const difference = length % Base64.SEGMENT_LENGTH

  if (!difference) {
    return string
  }

  let position = length
  let padLength = Base64.SEGMENT_LENGTH - difference
  const byteView = ByteView.alloc(length + padLength)
  byteView.write(string)

  while (padLength--) {
    byteView.write('=', position++)
  }

  return byteView.toString()
}

function parseLengths (string) {
  const { length } = string
  let validLength = string.indexOf('=')

  if (length % 4 > 0) {
    throw new Error('Invalid string. Length must be a multiple of 4')
  }

  if (validLength === -1) validLength = length

  const placeHoldersLength = validLength === length
    ? 0
    : Base64.SEGMENT_LENGTH - (validLength % Base64.SEGMENT_LENGTH)

  return [validLength, placeHoldersLength]// (((length - placeHoldersLength) * 6) / 8) | 0
}
